import type { FrameStatus, StepsState, StepState } from 'components/annotation-page/workspace/steps/steps-api';

import {
    formatDay, plural, pluralOf, t,
} from 'utils/i18n';

export { plural };

export interface StepsSummary {
    shown: StepState[];
    waiting: StepState[];
    done: number;
    total: number;
}

// the page counts the steps that can run; the ones waiting on something are named in one line
export function stepsSummary(state: StepsState | null): StepsSummary | null {
    if (!state) return null;
    const shown = state.steps.filter((step) => step.available && step.enabled);
    const waiting = state.steps.filter((step) => !step.available);
    return {
        shown, waiting, done: shown.filter((step) => step.status === 'done').length, total: shown.length,
    };
}

export function stepsLabel(summary: StepsSummary | null): string {
    if (!summary) return '';
    if (summary.total === 0) return t('no steps to run');
    if (summary.done === 0) return t('nothing run yet');
    if (summary.done === summary.total) return t('all steps done');
    return t('{done} of {total} steps done', { done: summary.done, total: summary.total });
}

export function stepNote(step: StepState, noun: string): string {
    if (step.status === 'failed') return t('{count} failed', { count: plural(step.counts.failed, noun) });
    if (step.status === 'running' || step.status === 'queued') return t('running');
    return pluralOf(step.counts.done, step.counts.total, noun);
}

export function waitingLine(waiting: StepState[]): string | null {
    if (!waiting.length) return null;
    const titles = waiting.map((step) => t(step.title));
    const last = titles[titles.length - 1];
    const names = titles.length > 1 ? `${titles.slice(0, -1).join(', ')}${t(' and ')}${last}` : last;
    if (titles.length > 1) return t('{names} are not available yet.', { names });
    return t('{names} is not available yet.', { names });
}

// one row of tiles, never a gallery: the last tile counts the rest
export function tiles(count: number, max: number): { shown: number[]; more: number } {
    if (count <= max) return { shown: Array.from({ length: count }, (_, i) => i), more: 0 };
    return { shown: Array.from({ length: max - 1 }, (_, i) => i), more: count - (max - 1) };
}

export function tileStatus(status: Record<string, FrameStatus> | undefined, frame: number): FrameStatus | null {
    return status?.[String(frame)] ?? null;
}

// "Oct 6, 2026 by admin"
export function createdLabel(date: string, user: string | undefined): string {
    const day = formatDay(date);
    return user ? t('{date} by {user}', { date: day, user }) : day;
}
